// Shared, localStorage-backed registry of in-flight DFT jobs.
//
// LatticeWizard's "Run DFT, then generate" chain (src/components/LatticeWizard.jsx)
// and AppShell's Analyze ▾ > <DFT kind> panel (DftAsyncPanel in src/AppShell.jsx)
// each submit and poll DFT jobs independently, in separate component subtrees that
// don't share React state. Before this registry existed, submitting a job from one
// surface and then opening the other showed a fresh "Submit DFT Job" button with no
// indication anything was running: closing the wizard mid-job lost the job id
// entirely, and reopening it (or opening the Analyze panel for the same candidate)
// had no way to find it again.
//
// This module is the one thing both surfaces read and write. Entries are keyed by
// `${candidateFingerprint}::${kind}`, using the SAME candidate identity as
// AppShell's `_fpFrame`, AppProvider's `_fingerprint`, and firebase/db.js's
// `_canonicalFingerprint` (lattice-based: `formula_L:a_b_c`, falling back to
// `formula_N:atomCount`, or `mol_<smiles>` for molecules) -- callers own computing
// that fingerprint and building the key; this module only stores against it.
//
// Storage is best-effort: any localStorage failure (unavailable, disabled in a
// private/sandboxed context, quota exceeded) is swallowed and treated as an empty
// registry. Losing this state only means a surface falls back to showing "Submit"
// again -- never a crash.

const PREFIX = 'dftJobRegistry::';

function getStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    // Some browsers throw merely on *accessing* localStorage (e.g. certain
    // private-browsing / sandboxed configurations), not just on use.
    return null;
  }
}

// Register (or overwrite) the in-flight job for `key`. `jobId` is the id the
// DFT service returned from its submit endpoint. Records the current time as
// submittedAt so a resuming surface's elapsed timer starts from the real
// submission time, not from when it happened to reopen.
//
// `options.autoGenerate` records whether this job was submitted as part of
// LatticeWizard's "Run DFT, then generate" chain (dft_first path) and should
// therefore auto-submit the lattice job once it completes. Only that call
// site should ever pass `autoGenerate: true`; AppShell's Analyze panel
// submits DFT jobs standalone (the user never asked for a lattice run) and
// must leave it at the default `false`, or a candidate the user only meant
// to analyze would silently fire an unrequested generation the moment
// LatticeWizard later happens to adopt that same job.
export function registerJob(key, jobId, options) {
  const storage = getStorage();
  if (!storage || !key || !jobId) return;
  const autoGenerate = !!(options && options.autoGenerate);
  try {
    storage.setItem(PREFIX + key, JSON.stringify({ jobId, submittedAt: Date.now(), autoGenerate }));
  } catch (_) {
    // Quota exceeded, storage disabled, etc: in-flight visibility is
    // best-effort and never worth surfacing an error for.
  }
}

// Look up the in-flight job for `key`. Returns { jobId, submittedAt,
// autoGenerate } or null if there is no entry, the entry is malformed, or
// storage is unavailable. Defensive about content: this reads back whatever a
// prior page load wrote, which could be stale, hand-edited, or corrupted --
// including an entry written before `autoGenerate` existed, which defaults to
// false (the safe choice: never auto-submit a lattice run nobody asked for).
export function getJob(key) {
  const storage = getStorage();
  if (!storage || !key) return null;
  let raw;
  try {
    raw = storage.getItem(PREFIX + key);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || !parsed.jobId) return null;
  return {
    jobId: parsed.jobId,
    submittedAt: typeof parsed.submittedAt === 'number' ? parsed.submittedAt : null,
    autoGenerate: !!parsed.autoGenerate,
  };
}

// Remove the in-flight entry for `key`. Callers clear this once a job reaches
// a terminal state (complete/error/cancelled) so a later resume attempt
// doesn't keep re-adopting a job that's already done, and defensively on
// explicit user cancel/stop actions.
export function clearJob(key) {
  const storage = getStorage();
  if (!storage || !key) return;
  try {
    storage.removeItem(PREFIX + key);
  } catch (_) {
    // Best-effort.
  }
}
